/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import './DetailSummaryComponentTheme.scss';
import { formatCurrency, formatPriceValue, formatUnit } from '../Utils/Utils';
import { Badge, Icon, Tooltip } from '@scuf/common';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../Utils/Constants';
import {
  getConvertedUnit,
  getConvertedValues,
  getNullToZero
} from '../Utils/EnergyUsagesViewDetailsUtils';
import { SmartPowerSubscriptionType, EnergyUsageField } from '../Enums';
import { getSummaryBadgeColor } from '../Utils/Graph/AllGraphUtils';
import { getPercentageColor } from '../../DetailKPIWidgets/PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { scaledData } from '../ProgressBar/IProgressBarWidget';
import { UserContext } from '../../DetailKPIWidgets/CarbonSiteViewDetailsComponent/TotalEmissionDetailWidget/TotalEmissionDetailWidget';
import {
  IAny,
  IRequestContext
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import _ from 'lodash';
import { ApiContext } from '../../../context/ApiContext';

interface IDetailSummaryComponentProps {
  aggregationData: IDetailAggregationData;
  measurementUnit?: string;
  costCurrency?: string;
  detailType: string;
  category?: string;
  deviationScaledData?: scaledData;
  isCarbonAvoidance?: boolean;
  renderedFromCarbon?: boolean;
  fromCarbonScopeOne?: boolean;
  sourceType?: string;
  waterAvgUnit?: string;
  operationalTimePercentage?: number;
  timeUsedForBaseline?: number;
  opportunityFromEO?: number;
  opportunityUnit?: string;
  opportunityConverted?: number;
  baselineCostUnit?: string;
}

export interface IDetailAggregationData {
  actualCost: number | null;
  baselineCost: number | null;
  actualAvoidanceCost?: number | null;
  baselineAvoidanceCost?: number | null;
  costSavings: number | null;
  avoidanceCostSavings?: number | null;
  costSavingsPercentage: number | null;
  avoidanceCostSavingsPercentage?: number | null;
  eoEnableDaysSavingPercentage?: number | null;
  entirePeriodSavingPercentage?: number | null;
  actualUnit?: string;
  baselineUnit?: string;
  changeUnit?: string;
  eoCostSavingsUnit?: string;
  // #TODO Water Meter remove undefined during api integration
  avgDailyConsumption?: number | null;
  avgDailyConsumptionUnit?: string;
  avgMonthlyConsumption?: number | null;
  avgMonthlyConsumptionUnit?: string;
  monthlyConsumptionPersqft?: number | null;
  monthlyConsumptionPersqftUnit?: string;
}

const categoryCheckForEmaasSummaryTitle = (
  category: string | undefined,
  t: IAny,
  renderedFromCarbon?: boolean,
  isFromCarbonAviodance?: boolean,
  sourceType?: string
) => {
  const d = descriptors.energyManagement;
  if (category === EnergyUsageField.ENERGY_USAGE) {
    return _.toLower(sourceType) === 'water'
      ? t(d.PREVIOUSPERIOD)
      : t(d.BASELINE);
  } else {
    return renderedFromCarbon
      ? isFromCarbonAviodance
        ? t(d.PREVIOUSAVOIDANCE)
        : t(d.PREVIOUSEMISSIONS)
      : t(d.PREVIOUSPERIOD);
  }
};

const detailViewSelectDisplay = (
  aggregationData: IDetailAggregationData,
  detailType: string,
  detailViewSelect: {
    (type: string): JSX.Element;
    (arg0: string):
      | boolean
      | React.ReactChild
      | React.ReactFragment
      | React.ReactPortal
      | null
      | undefined;
  }
) => {
  return (
    aggregationData && (
      <div className="emaas-summary-widget-container">
        {detailViewSelect(detailType)}
      </div>
    )
  );
};

const formattedSavingsPercentageCheck = (
  costSavings: number | null,
  formattedSavingsPercentage: number
) => {
  if (costSavings === null) {
    return '--';
  } else {
    return (
      <span
        className="emaas-saving-percentage"
        style={{
          color: getPercentageColor(formattedSavingsPercentage)
        }}
      >
        <Badge color={getSummaryBadgeColor(formattedSavingsPercentage)} empty />
        <span> {Math.abs(formattedSavingsPercentage)}%</span>
      </span>
    );
  }
};

const eoEnableDaysUptimePercentageCheck = (
  operationalTimePercentage: number | null | undefined,
  timeUsedForBaseline: number | null | undefined,
  t: IAny,
  toggleEnableEoStatus?: boolean
) => {
  const d = descriptors.energyManagement;
  return (
    operationalTimePercentage !== undefined && (
      <>
        <div className="eo-enable-align">
          <div className="summary-eo-enable">
            {t(d.operationalTimeEO, {
              opertionalPercentage: parseFloat(
                getNullToZero(operationalTimePercentage).toFixed(
                  KPI_PERCENTAGE_DECIMAL_PRECISION
                )
              )
            })}{' '}
            {toggleEnableEoStatus &&
              timeUsedForBaseline !== undefined &&
              timeUsedForBaseline !== null &&
              timeUsedForBaseline !== 0 && <>+</>}
          </div>
          <div className="summary-eo-entire">
            {timeUsedForBaseline !== undefined &&
              timeUsedForBaseline !== null &&
              timeUsedForBaseline !== 0 &&
              toggleEnableEoStatus &&
              t(d.timeUsedForBaselining, {
                baseline: parseFloat(
                  getNullToZero(timeUsedForBaseline as IAny).toFixed(
                    KPI_PERCENTAGE_DECIMAL_PRECISION
                  )
                )
              })}
          </div>
        </div>
      </>
    )
  );
};

const eoEnableDaysSavingPercentageCheck = (
  eoEnableDaysSavingPercentage: number | null | undefined,
  t: IAny
) => {
  const d = descriptors.energyManagement;
  return (
    eoEnableDaysSavingPercentage !== undefined && (
      <>
        {/* <div className="section-divider" /> */}
        <div className="summary-eo-enable">
          {parseFloat(
            getNullToZero(eoEnableDaysSavingPercentage)?.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          )}
          % {t(d.EOSAVINGONENABLE)}
        </div>
      </>
    )
  );
};

const entirePeriodSavingPercentageCheck = (
  entirePeriodSavingPercentage: number | null | undefined,
  t: IAny
) => {
  const d = descriptors.energyManagement;
  return (
    entirePeriodSavingPercentage !== undefined && (
      <>
        {/* <div className="section-divider" /> */}
        <div className="summary-eo-entire">
          {parseFloat(
            getNullToZero(entirePeriodSavingPercentage)?.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          )}
          % {t(d.EOSAVINGENTIREPERIOD)}
        </div>
      </>
    )
  );
};
const avoidanceCostSavingsPercentageCheck = (
  avoidanceCostSavingsPercentage: number | null | undefined
) => avoidanceCostSavingsPercentage ?? 0;
const actualAvoidanceCostCheck = (
  actualAvoidanceCost: number | null | undefined
) => actualAvoidanceCost ?? 0.0;
const baselineAvoidanceCostCheck = (
  baselineAvoidanceCost: number | null | undefined
) => baselineAvoidanceCost ?? 0.0;
const isPremiumSubscriptionCheck = (
  isCarbonAvoidance: boolean,
  requestContext: IRequestContext | undefined
) =>
  isCarbonAvoidance
    ? true
    : requestContext?.context?.smpSubscriptionType ===
      SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? true
    : false;
const DetailSummaryComponent = (props: IDetailSummaryComponentProps) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  let isPremiumSubscription: boolean = false;

  const apiContext = useContext(ApiContext);
  const {
    isFromSmartPower,
    toggleOpportunityFromEO,
    toggleEnableEoStatus
  } = apiContext!;

  const {
    aggregationData,
    costCurrency,
    detailType,
    measurementUnit,
    baselineCostUnit,
    category,
    deviationScaledData,
    isCarbonAvoidance,
    sourceType,
    waterAvgUnit,
    operationalTimePercentage,
    timeUsedForBaseline,
    opportunityFromEO,
    opportunityConverted,
    opportunityUnit
  } = props;
  const {
    actualCost,
    baselineCost,
    actualAvoidanceCost,
    baselineAvoidanceCost,
    costSavings,
    avoidanceCostSavings,
    costSavingsPercentage,
    avoidanceCostSavingsPercentage,
    eoEnableDaysSavingPercentage,
    entirePeriodSavingPercentage,
    actualUnit,
    baselineUnit,
    changeUnit,
    eoCostSavingsUnit,
    avgDailyConsumption,
    avgMonthlyConsumption,
    monthlyConsumptionPersqft,
    avgDailyConsumptionUnit,
    avgMonthlyConsumptionUnit,
    monthlyConsumptionPersqftUnit
  } = aggregationData;
  const formattedSavingsPercentage = parseFloat(
    getNullToZero(costSavingsPercentage)?.toFixed(
      KPI_PERCENTAGE_DECIMAL_PRECISION
    )
  );
  const formattedAvoidanceSavingsPercentage = parseFloat(
    getNullToZero(
      avoidanceCostSavingsPercentageCheck(avoidanceCostSavingsPercentage)
    )?.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
  );

  if (isCarbonAvoidance !== undefined) {
    const requestContext = useContext(UserContext);
    isPremiumSubscription = isPremiumSubscriptionCheck(
      isCarbonAvoidance,
      requestContext
    );
  }

  const detailViewSelect = (type: string) => {
    switch (type) {
      case 'utilitydetail':
        return (
          <div className="summary-cost emaas-detail-summary-card-section">
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {formatCurrency(actualCost!, costCurrency)}
                  {/* {costCurrency === 'MYR'
                    ? formatCurrency(actualCost!, costCurrency)
                    : formatPriceValue(
                        formatCurrency(actualCost!, costCurrency)
                      )} */}
                </span>
                {/* {costCurrency !== 'MYR' && (
                  <span className="emaas-summary-unit">
                    {formatUnit(formatCurrency(actualCost!, costCurrency))}
                  </span>
                )} */}
              </div>
              <span className="emaas-summary-title">{t(d.ACTUALCOST)}</span>
            </div>
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value baseline-cost">
                  {formatCurrency(baselineCost!, costCurrency)}
                  {/* {costCurrency === 'MYR'
                    ? formatCurrency(baselineCost!, costCurrency)
                    : formatPriceValue(
                        formatCurrency(baselineCost!, costCurrency)
                      )} */}
                </span>
                {/* {costCurrency !== 'MYR' && (
                  <span className="emaas-summary-unit">
                    {formatUnit(formatCurrency(baselineCost!, costCurrency))}
                  </span>
                )} */}
              </div>
              <span className="emaas-summary-title">{t(d.PREVIOUSCOST)}</span>
            </div>
            <div className="summary-savings-cost emaas-summary-value-container">
              {(apiContext.showChangeWhenData &&
                (isNaN(Number(actualCost)) ||
                  isNaN(Number(baselineCost)) ||
                  Number(actualCost) === 0 ||
                  Number(baselineCost) === 0 ||
                  Math.abs(Number(costSavingsPercentage)) >
                    PERCENT_CHANGE_THRESHOLD)) ||
              costSavingsPercentage === null ? (
                '--'
              ) : (
                <div>
                  <span
                    className="emaas-summary-value cost-saving"
                    style={{
                      color: getPercentageColor(formattedSavingsPercentage)
                    }}
                  >
                    {formatCurrency(costSavings!, costCurrency)}
                    {/* {costCurrency === 'MYR'
                      ? formatCurrency(costSavings!, costCurrency)
                      : formatPriceValue(
                          formatCurrency(costSavings!, costCurrency)
                        )} */}
                  </span>
                  {/* {costCurrency !== 'MYR' && (
                    <span
                      className="emaas-summary-unit"
                      style={{
                        color: getPercentageColor(formattedSavingsPercentage)
                      }}
                    >
                      {formatUnit(formatCurrency(costSavings!, costCurrency))}
                    </span>
                  )} */}
                  <span
                    className="emaas-saving-percentage"
                    style={{
                      color: getPercentageColor(formattedSavingsPercentage)
                    }}
                  >
                    <Badge
                      color={getSummaryBadgeColor(formattedSavingsPercentage)}
                      empty
                    />
                    <span> {Math.abs(formattedSavingsPercentage)}%</span>
                  </span>
                </div>
              )}
              <span className="emaas-summary-title">{t(d.CHANGE)}</span>
            </div>
          </div>
        );
      case 'detailSummaryWithoutCurrency':
        return (
          <div className="summary-cost emaas-detail-summary-card-section">
            {!isCarbonAvoidance && (
              <>
                <div className="summary-actual-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span className="emaas-summary-value actual-cost">
                      {getConvertedValues(actualCost)}
                    </span>
                    <span className="emaas-summary-unit">
                      {props.fromCarbonScopeOne
                        ? actualUnit
                        : getConvertedUnit(actualCost, measurementUnit)}
                    </span>
                  </div>
                  <span className="emaas-summary-title">
                    {props.renderedFromCarbon
                      ? t(d.ACTUALEMISSIONS)
                      : t(d.ACTUAL)}
                  </span>
                </div>
                <div className="summary-baseline-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span className="emaas-summary-value baseline-cost">
                      {getConvertedValues(baselineCost)}
                    </span>
                    <span className="emaas-summary-unit">
                      {props.fromCarbonScopeOne
                        ? baselineUnit
                        : getConvertedUnit(baselineCost, measurementUnit)}
                    </span>
                  </div>
                  <span className="emaas-summary-title">
                    {categoryCheckForEmaasSummaryTitle(
                      category,
                      t,
                      props.renderedFromCarbon,
                      false,
                      sourceType || ''
                    )}
                  </span>
                </div>
                <div className="summary-savings-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span
                      className="emaas-summary-value cost-saving"
                      style={
                        costSavings !== null
                          ? {
                              color: getPercentageColor(
                                formattedSavingsPercentage
                              )
                            }
                          : {}
                      }
                    >
                      {getConvertedValues(costSavings, deviationScaledData)}
                    </span>

                    <span className="emaas-summary-unit">
                      <span
                        style={
                          costSavings !== null
                            ? {
                                color: getPercentageColor(
                                  formattedSavingsPercentage
                                )
                              }
                            : {}
                        }
                      >
                        {getConvertedUnit(
                          costSavings,
                          props.fromCarbonScopeOne
                            ? changeUnit
                            : measurementUnit,
                          deviationScaledData
                        )}
                      </span>
                    </span>
                    {formattedSavingsPercentageCheck(
                      costSavings,
                      formattedSavingsPercentage
                    )}
                  </div>
                  <span className="emaas-summary-title">{t(d.CHANGE)}</span>
                </div>
                {_.toLower(sourceType) === 'water' && (
                  <>
                    {!_.isUndefined(avgMonthlyConsumption) &&
                      !_.isNull(avgMonthlyConsumption) && (
                        <div className="summary-actual-cost emaas-summary-value-container smp-co2">
                          <div>
                            <span className="emaas-summary-value actual-cost">
                              {getConvertedValues(
                                _.isUndefined(avgMonthlyConsumption)
                                  ? null
                                  : avgMonthlyConsumption
                              )}
                            </span>
                            <span className="emaas-summary-unit">
                              {getConvertedUnit(
                                _.isUndefined(avgMonthlyConsumption)
                                  ? null
                                  : avgMonthlyConsumption,
                                avgMonthlyConsumptionUnit || measurementUnit
                              )}
                            </span>
                          </div>
                          <span className="emaas-summary-title">
                            {t(d.AVG_CONSUMP_PER_MTH)}
                          </span>
                        </div>
                      )}
                    {!_.isUndefined(avgDailyConsumption) &&
                      !_.isNull(avgDailyConsumption) && (
                        <div className="summary-baseline-cost emaas-summary-value-container smp-co2">
                          <div>
                            <span className="emaas-summary-value baseline-cost">
                              {getConvertedValues(
                                _.isUndefined(avgDailyConsumption)
                                  ? null
                                  : avgDailyConsumption
                              )}
                            </span>
                            <span className="emaas-summary-unit">
                              {getConvertedUnit(
                                _.isUndefined(avgDailyConsumption)
                                  ? null
                                  : avgDailyConsumption,
                                avgDailyConsumptionUnit || measurementUnit
                              )}
                            </span>
                          </div>
                          <span className="emaas-summary-title">
                            {t(d.AVG_CONSUMP_PER_DAY)}
                          </span>
                        </div>
                      )}
                    {!_.isUndefined(monthlyConsumptionPersqft) &&
                      !_.isNull(monthlyConsumptionPersqft) && (
                        <div className="summary-baseline-cost emaas-summary-value-container smp-co2">
                          <div>
                            <span className="emaas-summary-value baseline-cost">
                              {getConvertedValues(
                                _.isUndefined(monthlyConsumptionPersqft)
                                  ? null
                                  : monthlyConsumptionPersqft
                              )}
                            </span>
                            <span className="emaas-summary-unit">
                              {getConvertedUnit(
                                _.isUndefined(monthlyConsumptionPersqft)
                                  ? null
                                  : monthlyConsumptionPersqft,
                                monthlyConsumptionPersqftUnit || measurementUnit
                              )}
                            </span>
                          </div>
                          <span className="emaas-summary-title">
                            {t(d.MTHLY_CONSUMP_PER_SQFT)}
                          </span>
                        </div>
                      )}
                  </>
                )}
              </>
            )}
            {isPremiumSubscription && (
              <>
                <div className="summary-actual-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span className="emaas-summary-value actual-cost">
                      {getConvertedValues(
                        actualAvoidanceCostCheck(actualAvoidanceCost)
                      )}
                    </span>
                    <span className="emaas-summary-unit">
                      {getConvertedUnit(
                        actualAvoidanceCostCheck(actualAvoidanceCost),
                        measurementUnit
                      )}
                    </span>
                  </div>
                  <span className="emaas-summary-title">
                    {t(d.ACTUALAVOIDANCE)}
                  </span>
                </div>
                <div className="summary-baseline-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span className="emaas-summary-value baseline-cost">
                      {getConvertedValues(
                        baselineAvoidanceCostCheck(baselineAvoidanceCost)
                      )}
                    </span>
                    <span className="emaas-summary-unit">
                      {getConvertedUnit(
                        baselineAvoidanceCostCheck(baselineAvoidanceCost),
                        measurementUnit
                      )}
                    </span>
                  </div>
                  <span className="emaas-summary-title">
                    {categoryCheckForEmaasSummaryTitle(category, t, true, true)}
                  </span>
                </div>
                <div className="summary-savings-cost emaas-summary-value-container smp-co2">
                  <div>
                    <span className="emaas-summary-value cost-saving">
                      {getConvertedValues(
                        avoidanceCostSavings ?? 0.0,
                        deviationScaledData
                      )}
                    </span>

                    <span className="emaas-summary-unit">
                      {getConvertedUnit(
                        avoidanceCostSavings ?? 0.0,
                        measurementUnit,
                        deviationScaledData
                      )}
                    </span>
                    {avoidanceCostSavings === null ? (
                      ' '
                    ) : (
                      <span
                        className="emaas-saving-percentage"
                        style={{
                          color: getPercentageColor(
                            formattedAvoidanceSavingsPercentage
                          )
                        }}
                      >
                        <Badge
                          color={getSummaryBadgeColor(
                            formattedAvoidanceSavingsPercentage
                          )}
                          empty
                        />
                        <span>
                          {' '}
                          {Math.abs(formattedAvoidanceSavingsPercentage)}%
                        </span>
                      </span>
                    )}
                  </div>
                  <span className="emaas-summary-title">{t(d.CHANGE)}</span>
                </div>
              </>
            )}
          </div>
        );
      case 'energyOptimizationSummary':
        return (
          <div className="summary-cost emaas-detail-summary-card-section">
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {formatPriceValue(formatCurrency(actualCost!, costCurrency))}
                </span>
                <span className="emaas-summary-unit">
                  {formatUnit(formatCurrency(actualCost!, costCurrency))}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.ACTUAL)}</span>
            </div>
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value baseline-cost">
                  {formatPriceValue(
                    formatCurrency(baselineCost!, costCurrency)
                  )}
                </span>
                <span className="emaas-summary-unit">
                  {formatUnit(formatCurrency(baselineCost!, costCurrency))}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.CALCULATED)}</span>
            </div>
            <div className="summary-savings-cost summary-estimated-savings emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value cost-saving">
                  {formatPriceValue(
                    formatCurrency(costSavings!, costCurrency, false)
                  )}
                </span>
                <span className="emaas-summary-unit">
                  {formatUnit(formatCurrency(costSavings!, costCurrency))}
                </span>
                <span
                  className="emaas-saving-percentage"
                  style={{
                    color: getPercentageColor(formattedSavingsPercentage)
                  }}
                >
                  <Badge
                    color={getSummaryBadgeColor(formattedSavingsPercentage)}
                    empty={true}
                  />
                  <span> {Math.abs(formattedSavingsPercentage)}%</span>
                </span>
              </div>
              <div className="emaas-summary-title">
                {t(d.ESTIMATEDSAVINGS)}
                <Tooltip
                  element={
                    <Icon root="common" name="badge-info" size="small" />
                  }
                  event="hover"
                  content={t(d.EOSAVINGTOOLTIP)}
                  position="right center"
                  size="small"
                />
              </div>
            </div>
            {toggleOpportunityFromEO && (
              <div className="summary-savings-cost emaas-summary-value-container">
                <div>
                  <span className="emaas-summary-value actual-cost">
                    {opportunityFromEO === null
                      ? '--'
                      : opportunityFromEO === 0
                      ? formatPriceValue(formatCurrency(0, costCurrency))
                      : formatPriceValue(
                          formatCurrency(opportunityFromEO!, costCurrency)
                        )}
                  </span>
                  <span className="emaas-summary-unit">
                    {opportunityFromEO !== null && opportunityFromEO === 0
                      ? formatUnit(formatCurrency(0, costCurrency))
                      : formatUnit(
                          formatCurrency(opportunityFromEO!, costCurrency)
                        )}
                    {/* {formatUnit(
                      formatCurrency(opportunityFromEO!, costCurrency)
                    )} */}
                  </span>
                </div>
                <span className="emaas-summary-title">
                  {t(d.OPPORTUNITYFROMEO)}
                </span>
              </div>
            )}
            <div className="section-divider" />
            {eoEnableDaysUptimePercentageCheck(
              operationalTimePercentage,
              timeUsedForBaseline,
              t,
              toggleEnableEoStatus
            )}
            <div className="section-divider" />

            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {eoEnableDaysSavingPercentageCheck(
                eoEnableDaysSavingPercentage,
                t
              )}
              {entirePeriodSavingPercentageCheck(
                entirePeriodSavingPercentage,
                t
              )}
            </div>
          </div>
        );
      case 'energyOptimizationSummaryUtilityConsumption': {
        return (
          <div className="summary-cost emaas-detail-summary-card-section">
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {getConvertedValues(actualCost)}
                </span>
                <span className="emaas-summary-unit">
                  {getConvertedUnit(actualCost, measurementUnit)}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.ACTUAL)}</span>
            </div>
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {getConvertedValues(baselineCost)}
                </span>
                <span className="emaas-summary-unit">
                  {getConvertedUnit(
                    baselineCost,
                    baselineCostUnit || measurementUnit
                  )}
                </span>
              </div>
              <span className="emaas-summary-title">{t(d.CALCULATED)}</span>
            </div>
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {getConvertedValues(costSavings)}
                </span>
                <span className="emaas-summary-unit">
                  {getConvertedUnit(
                    costSavings,
                    eoCostSavingsUnit || measurementUnit
                  )}
                </span>
                <span
                  className="emaas-saving-percentage"
                  style={{
                    color: getPercentageColor(formattedSavingsPercentage)
                  }}
                >
                  <Badge
                    color={getSummaryBadgeColor(formattedSavingsPercentage)}
                    empty={true}
                  />
                  <span> {Math.abs(formattedSavingsPercentage)}%</span>
                </span>
              </div>
              <div className="emaas-summary-title">
                {t(d.ESTIMATEDSAVINGS)}
                <Tooltip
                  element={
                    <Icon root="common" name="badge-info" size="small" />
                  }
                  event="hover"
                  content={t(d.EOSAVINGTOOLTIP)}
                  position="right center"
                  size="small"
                />
              </div>
            </div>
            {toggleOpportunityFromEO && (
              <div className="summary-savings-cost emaas-summary-value-container">
                <div>
                  <span className="emaas-summary-value actual-cost">
                    {opportunityFromEO === null ? '--' : opportunityConverted}
                  </span>
                  <span className="emaas-summary-unit">
                    {opportunityFromEO !== null && opportunityUnit}
                  </span>
                </div>
                <span className="emaas-summary-title">
                  {t(d.OPPORTUNITYFROMEO)}
                </span>
              </div>
            )}
            <div className="section-divider" />
            {eoEnableDaysUptimePercentageCheck(
              operationalTimePercentage,
              timeUsedForBaseline,
              t,
              toggleEnableEoStatus
            )}
            <div className="section-divider" />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {eoEnableDaysSavingPercentageCheck(
                eoEnableDaysSavingPercentage,
                t
              )}
              {entirePeriodSavingPercentageCheck(
                entirePeriodSavingPercentage,
                t
              )}
            </div>
          </div>
        );
      }
      case 'detailSummaryWithoutCurrencyNILM':
        return (
          <div className="summary-cost emaas-detail-summary-card-section">
            <div className="summary-actual-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value actual-cost">
                  {getConvertedValues(actualCost)}
                </span>
                <span className="emaas-summary-unit">
                  {props.fromCarbonScopeOne
                    ? actualUnit
                    : getConvertedUnit(actualCost, measurementUnit)}
                </span>
              </div>
              <span className="emaas-summary-title">
                {props.renderedFromCarbon ? t(d.ACTUALEMISSIONS) : t(d.ACTUAL)}
              </span>
            </div>
            {/* Remove hideDid from className if we want to display Previous period for NILM */}
            <div className="summary-baseline-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value baseline-cost">
                  {getConvertedValues(baselineCost)}
                </span>
                <span className="emaas-summary-unit">
                  {props.fromCarbonScopeOne
                    ? baselineUnit
                    : getConvertedUnit(baselineCost, measurementUnit)}
                </span>
              </div>
              <span className="emaas-summary-title">
                {categoryCheckForEmaasSummaryTitle(
                  category,
                  t,
                  props.renderedFromCarbon
                )}
              </span>
            </div>
            {/* Remove hideDid from className if we want to display change for NILM */}
            <div className="summary-savings-cost emaas-summary-value-container">
              <div>
                <span className="emaas-summary-value cost-saving">
                  {getConvertedValues(costSavings, deviationScaledData)}
                </span>

                <span className="emaas-summary-unit">
                  {props.fromCarbonScopeOne
                    ? changeUnit
                    : getConvertedUnit(
                        costSavings,
                        measurementUnit,
                        deviationScaledData
                      )}
                </span>
                {costSavings === null ? (
                  ' '
                ) : (
                  <span
                    className="emaas-saving-percentage"
                    style={{
                      color: getPercentageColor(formattedSavingsPercentage)
                    }}
                  >
                    <Badge
                      color={getSummaryBadgeColor(formattedSavingsPercentage)}
                      empty
                    />
                    <span> {Math.abs(formattedSavingsPercentage)}%</span>
                  </span>
                )}
              </div>
              <span className="emaas-summary-title">{t(d.CHANGE)}</span>
            </div>
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <div>
      {detailViewSelectDisplay(aggregationData, detailType, detailViewSelect)}
    </div>
  );
};

export default DetailSummaryComponent;
